import {
  getApiSamplesV1SamplesEvidenceListQueryKey,
  getApiSamplesV1SamplesListRetrieveQueryKey,
  getApiSamplesV1SamplesRetrieveQueryKey,
  getSampleVariantFromMaterial,
  isFieldErrorResponse,
  SampleType,
  type UpdateSampleBreakdownDTO,
  useApiSamplesV1SamplesEvidenceListSuspense,
  useApiSamplesV1SamplesRetrieveSuspense,
  useApiSamplesV1SamplesUpdate,
  useQueryClient,
} from 'api';
import {
  ActionBar,
  SampleFormFields,
  TotalWeightWarning,
  useManageEvidence,
  useNotification,
  useSiteMaterial,
} from 'features';
import { Form, useForm, useWatch } from 'form';
import { useTranslation } from 'i18n';
import { Suspense } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ActionRow,
  Box,
  Button,
  Grid,
  ListSkeletonLoader,
  Page,
  PhotoEvidenceMultiSelectList,
  Section,
  Stack,
  Typography,
  useBreakpoint,
} from 'ui';

import { SiteHeader } from '../../components/SiteHeader/SiteHeader';

type FormValues = {
  description: string;
  total_weight: number | null;
  breakdown: UpdateSampleBreakdownDTO[];
};

export const SampleEdit = () => {
  const { t } = useTranslation('orders');
  const { sampleId } = useParams();
  const { isMobile } = useBreakpoint();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { open } = useNotification();
  const { data: siteMaterial } = useSiteMaterial();
  const variant = getSampleVariantFromMaterial(siteMaterial.material_spec.title);
  const { data: sample } = useApiSamplesV1SamplesRetrieveSuspense(Number(sampleId));
  const { data: evidence } = useApiSamplesV1SamplesEvidenceListSuspense(Number(sampleId), { page_size: 10000 });
  const {
    selected,
    setSelected,
    deleteSelected,
    isPending: isDeletePending,
  } = useManageEvidence({
    mode: 'sample',
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getApiSamplesV1SamplesEvidenceListQueryKey(Number(sampleId)),
      });
    },
    parentId: Number(sampleId),
  });
  const form = useForm<FormValues>({ defaultValues: sample });
  const { mutate, isPending: isUpdatePending } = useApiSamplesV1SamplesUpdate({
    mutation: {
      onError: (error) => {
        if (isFieldErrorResponse(error)) {
          Object.keys(error.response?.data).forEach((field) => {
            form.setError(field as keyof FormValues, { message: error.response.data[field] });
          });
        } else {
          open({
            message: t('sampleEditPage.notifications.updateError', { values: { error: String(error) } }),
            type: 'error',
          });
        }
      },
      onSuccess: () => {
        open({
          message: t('sampleEditPage.notifications.updateSuccess'),
          type: 'success',
        });
        queryClient.invalidateQueries({
          queryKey: getApiSamplesV1SamplesListRetrieveQueryKey(siteMaterial.public_id),
        });
        queryClient.invalidateQueries({
          queryKey: getApiSamplesV1SamplesRetrieveQueryKey(sample.id),
        });
        navigate(-1);
      },
    },
  });
  const isPending = isDeletePending || isUpdatePending;

  const breakdownValues = useWatch({
    control: form.control,
    name: 'breakdown',
  });

  const totalWeight = useWatch({
    control: form.control,
    name: 'total_weight',
  });

  return (
    <Page>
      <Page.Title title={t('sampleEditPage.pageTitle')} />
      <Page.Divider />
      <Grid container>
        <Grid
          size={{
            md: 4,
            xs: 12,
          }}
        >
          <Section title={t('sampleEditPage.sections.siteDetails')}>
            <SiteHeader siteMaterial={siteMaterial} />
          </Section>
        </Grid>
        <Grid
          size={{
            md: 8,
            xs: 12,
          }}
        >
          <Section title={t('sampleEditPage.sections.sample')}>
            <Form
              {...form}
              onSubmit={(data) =>
                mutate({
                  data: {
                    ...sample,
                    breakdown: data.breakdown,
                    description: data.description,
                    total_weight: data.total_weight,
                  },
                  sampleId: sample.id,
                })
              }
            >
              <Stack
                direction={isMobile ? 'column' : 'row'}
                sx={{
                  gap: 3,
                }}
              >
                <Stack
                  sx={[
                    isMobile ? {} : { width: '50%' },
                    {
                      gap: 2,
                    },
                  ]}
                >
                  <Typography variant="body1" fontWeight="bold">
                    {t('sampleEditPage.labels.sample')}
                  </Typography>
                  <TotalWeightWarning totalWeight={totalWeight} breakdownValues={breakdownValues || []} />
                  <Suspense fallback={<ListSkeletonLoader />}>
                    <SampleFormFields variant={variant} disableBreakdown={sample.type === SampleType.AG} />
                  </Suspense>
                </Stack>

                <Stack
                  sx={{
                    maxHeight: '50vh',
                    gap: 1,
                  }}
                >
                  <Typography variant="body1" fontWeight="bold">
                    {t('sampleEditPage.labels.sampleEvidence')}
                  </Typography>
                  {selected.length > 0 && (
                    <Box
                      sx={{
                        position: 'sticky',
                        top: 0,
                      }}
                    >
                      <ActionBar
                        selected={selected.length}
                        handleClose={() => setSelected([])}
                        handleDelete={deleteSelected}
                        actionPending={isPending}
                      />
                    </Box>
                  )}
                  <PhotoEvidenceMultiSelectList
                    slotProps={{
                      imageList: {
                        cols: isMobile ? 3 : 5,
                      },
                    }}
                    evidencePhotos={evidence.results.map(({ id, file, preview }) => ({
                      id,
                      src: (preview || file) ?? '',
                    }))}
                    selected={selected}
                    onSelectionChange={setSelected}
                  />
                </Stack>
              </Stack>

              <ActionRow>
                <Button size="large" fullWidth variant="secondary" onClick={() => navigate(-1)}>
                  {t('sampleEditPage.actions.cancel')}
                </Button>
                <Button size="large" fullWidth variant="primary" type="submit" loading={isPending}>
                  {t('sampleEditPage.actions.submit')}
                </Button>
              </ActionRow>
            </Form>
          </Section>
        </Grid>
      </Grid>
    </Page>
  );
};
